/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { useAtom } from 'jotai';
import { isCreateGroupModalOpenAtom, waGroupMetadataAtom } from '../../../../../wa-groups/states/groups';
import QSModal from '../../../../../../shared/components/molecules/modal';
import { Box, IconButton } from '@mui/material';
import { HEADER_HEIGHT } from '../../../../../../shared/typings';
import { QSButton, QSTypography } from '../../../../../../shared/components/atoms';
import { Close } from '@mui/icons-material';
import { QSTextInput } from '../../../../../../shared/components/molecules';
import { ChangeEvent, useMemo, useState } from 'react';
import WabaSelector from '../../../../../../shared/components/molecules/waba-selector';
import { WabaIntegration } from '../../../../../../shared/typings/waba';
import { useCustomNavigate } from '../../../../../../shared/hooks/use-conversation-navigate';
import { ROUTES } from '../../../../../../shared/constants/routes';
import { WAGroupJoinApprovalMode } from '../../../../../wa-groups/typings/groups';
import { createWAGroupApi, getWAGroupMetadata } from '../../../../../wa-groups/requests/groups';
import useQuery from '../../../../../../shared/hooks/useQuery';
import useAddonPurchase from '../../../../../../shared/hooks/use-addon-purchase';
import { PlanAddonTypes } from '../../../../../renew-plan/v1/typings';
import Loader from '../../../../../../shared/components/molecules/loader';
import WAGroupLimitReached from './WAGroupLimitReached';
import ErrorContainer from '../../../../../../shared/components/molecules/error';
import useNotifications from '../../../../../../shared/hooks/use-notifications';

type Props = {};

const MAX_GROUP_NAME = 128;
const MAX_GROUP_DESCRIPTION = 2048;

const CreateGroupModal = (props: Props) => {
  const [open, setOpen] = useAtom(isCreateGroupModalOpenAtom);

  const [inputData, setInputData] = useState({
    name: '',
    description: '',
  });
  const [selectedIntegration, setSelectedIntegration] = useState<WabaIntegration | null>(null);
  const [errors, setErrors] = useState<{ name?: string; integration?: string }>({});
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useCustomNavigate();
  const { addNotification } = useNotifications();
  const { loading: purchaseAddonLoading, purchaseAddon } = useAddonPurchase();

  const nameCharCount = inputData.name.length;
  const descriptionCharCount = inputData.description.length;

  const {
    requestData: { data: waGroupMetadata, loading: waGroupMetadataLoading, error: waGroupMetadataError },
  } = useQuery({
    requestAtom: waGroupMetadataAtom,
    queryFunction: getWAGroupMetadata,
  });

  const currentGroupsCount = waGroupMetadata?.currentGroupsCount ?? 0;
  const groupsLimit = waGroupMetadata?.groupsLimit ?? 0;
  const isGroupsLimitReached = Boolean(waGroupMetadata) && currentGroupsCount >= groupsLimit;

  const resetForm = () => {
    setInputData({
      name: '',
      description: '',
    });
    setErrors({});
    setSelectedIntegration(null);
    setApiError(null);
    setIsLoading(false);
  };

  const onClose = () => {
    setOpen(false);
    resetForm();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setInputData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === 'name' && errors.name) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleWabaSelect = (waba: WabaIntegration) => {
    setSelectedIntegration(waba);

    if (errors.integration) {
      setErrors((prev) => ({ ...prev, integration: undefined }));
    }
  };

  const isCreateDisabled = useMemo(() => {
    return !inputData.name.trim() || !selectedIntegration?.phoneNumber;
  }, [inputData.name, selectedIntegration?.phoneNumber]);

  const handleIncreaseLimit = async () => {
    try {
      await purchaseAddon(PlanAddonTypes.WA_Groups);
    } catch (error) {
      addNotification({
        message: (error as Error).message ?? 'An error occurred. Please try again',
        type: 'error',
      });
    }
  };

  const handleSubmit = async () => {
    const updatedErrors: { name?: string; integration?: string } = {};

    if (!inputData.name.trim()) {
      updatedErrors.name = 'Group name is required';
    }

    if (!selectedIntegration?.phoneNumber) {
      updatedErrors.integration = 'Please choose an API number';
    }

    if (Object.keys(updatedErrors).length > 0) {
      setErrors(updatedErrors);
      return;
    }

    try {
      setApiError(null);
      setIsLoading(true);
      const payload = {
        subject: inputData.name.trim(),
        description: inputData.description.trim() || undefined,
        integrationId: selectedIntegration?.id ?? '',
        joinApprovalMode: WAGroupJoinApprovalMode.AUTO_APPROVE,
      };
      const resp = await createWAGroupApi(payload);
      const integrationWabaNumber = resp.wabaNumber || selectedIntegration?.phoneNumber || '';
      navigate({
        pathname: `${ROUTES.CONVERSATIONS}${ROUTES.WA_GROUP.replace(
          ':integrationWabaNumber',
          integrationWabaNumber
        ).replace(':groupId', resp.groupId)}`,
      });
      onClose();
    } catch (error) {
      const errorMessage = (error as { message?: string })?.message ?? 'Something went wrong while creating the group.';
      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const renderContent = () => {
    if (waGroupMetadataLoading) {
      return (
        <Loader
          size={24}
          containerProps={{
            sx: {
              width: '100%',
              minHeight: '220px',
            },
          }}
        />
      );
    }

    if (waGroupMetadataError) {
      return <ErrorContainer error="Something went wrong" />;
    }

    if (isGroupsLimitReached) {
      return <WAGroupLimitReached currentGroupsCount={currentGroupsCount} groupsLimit={groupsLimit} />;
    }

    return (
      <>
        <Box
          css={css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <Box
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <QSTypography
              css={css`
                color: #2d2d2d;
                font-family: Inter;
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: normal;
              `}
            >
              Name
            </QSTypography>
            <QSTypography
              css={css`
                font-size: 12px;
                font-weight: 500;
                color: ${nameCharCount >= MAX_GROUP_NAME ? '#ef5350' : '#7a7a7a'};
              `}
            >
              {nameCharCount}/{MAX_GROUP_NAME}
            </QSTypography>
          </Box>
          <QSTextInput
            variant="outlined"
            placeholder="Group name"
            value={inputData.name}
            onChange={handleInputChange}
            error={Boolean(errors.name)}
            textInputProps={{
              name: 'name',
              inputProps: { maxLength: MAX_GROUP_NAME },
              sx: {
                background: '#ffffff',
                '.MuiOutlinedInput-root': {
                  fontSize: '14px',
                  borderRadius: '6px',
                  padding: '0 12px',
                  minHeight: '40px',
                  alignItems: 'center',
                  fieldset: {
                    borderColor: '#E0E0EA',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3f906d',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3f906d',
                    borderWidth: '1px',
                  },
                  'input::placeholder, textArea::placeholder': {
                    color: '#7A7A7A',
                    opacity: 1,
                  },
                },
                '.MuiOutlinedInput-input': {
                  padding: '0',
                },
              },
            }}
          />
          {errors.name ? (
            <QSTypography
              css={css`
                font-size: 12px;
                font-weight: 600;
                color: #d32f2f;
              `}
            >
              {errors.name}
            </QSTypography>
          ) : null}
        </Box>

        <Box
          css={css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <Box
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: baseline;
            `}
          >
            <QSTypography
              css={css`
                color: #2d2d2d;
                font-family: Inter;
                font-size: 12px;
                font-style: normal;
                font-weight: 400;
                line-height: normal;
              `}
            >
              Description (Optional)
            </QSTypography>
            <QSTypography
              css={css`
                font-size: 12px;
                font-weight: 500;
                color: ${descriptionCharCount >= MAX_GROUP_DESCRIPTION ? '#ef5350' : '#7a7a7a'};
              `}
            >
              {descriptionCharCount}/{MAX_GROUP_DESCRIPTION}
            </QSTypography>
          </Box>
          <QSTextInput
            variant="outlined"
            multiLine
            placeholder="Enter group description"
            value={inputData.description}
            onChange={handleInputChange}
            textInputProps={{
              name: 'description',
              inputProps: { maxLength: MAX_GROUP_DESCRIPTION },
              minRows: 4,
              maxRows: 10,
              sx: {
                background: '#ffffff',
                '.MuiOutlinedInput-root': {
                  fontSize: '14px',
                  borderRadius: '6px',
                  padding: '12px',
                  alignItems: 'flex-start',
                  fieldset: {
                    borderColor: '#E0E0EA',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3f906d',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3f906d',
                    borderWidth: '1px',
                  },
                  'input::placeholder, textArea::placeholder': {
                    color: '#7A7A7A',
                    opacity: 1,
                  },
                },
                '.MuiOutlinedInput-input': {
                  padding: 0,
                },
              },
            }}
          />
        </Box>

        <Box
          css={css`
            width: 100%;
            display: flex;
            flex-direction: column;
            gap: 4px;
          `}
        >
          <Box
            css={css`
              width: 100%;
            `}
          >
            <WabaSelector
              isDropdown
              showWabaSelectorEvenIfSingle
              onChangeWaba={handleWabaSelect}
              showWabaNumber={true}
              headerTitle="Choose API number"
              headerTitleStyles={{
                sx: {
                  color: '#2d2d2d',
                  fontFamily: 'Inter',
                  fontSize: '12px',
                  fontWeight: 400,
                },
              }}
              hoverBorderColor="#3f906d"
              shouldHideSelectorHeader={true}
              inputStylesSx={{
                fontSize: '14px',
                fontWeight: 500,
              }}
              containerProps={{
                style: {
                  width: '100%',
                  maxWidth: '100%',
                },
              }}
            />
          </Box>
          {errors.integration ? (
            <QSTypography
              css={css`
                font-size: 12px;
                font-weight: 600;
                color: #d32f2f;
              `}
            >
              {errors.integration}
            </QSTypography>
          ) : null}
        </Box>
        {apiError ? (
          <QSTypography
            css={css`
              font-size: 12px;
              font-weight: 600;
              color: #d32f2f;
            `}
          >
            {apiError.includes('Insufficient permissions')
              ? 'You don’t have permission to create a group for this API number. Please contact your organization owner.'
              : apiError}
          </QSTypography>
        ) : null}
      </>
    );
  };

  return (
    <QSModal
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') {
          return;
        }
        onClose();
      }}
    >
      <Box
        css={css`
          width: 100%;
          max-width: min(90%, 550px);
          max-height: 90%;
          border-radius: 8px;
          color: #2d2d2d;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          background-color: #fdfdfd;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          outline: none;
        `}
      >
        {isGroupsLimitReached && (
          <IconButton
            onClick={onClose}
            css={css`
              position: absolute;
              top: 12px;
              right: 18px;
              z-index: 1;
            `}
            sx={{ marginRight: '-8px' }}
          >
            <Close
              css={css`
                font-size: 24px;
                color: #2d2d2d;
              `}
            />
          </IconButton>
        )}
        {!isGroupsLimitReached && (
          <Box
            css={css`
              height: ${HEADER_HEIGHT}px;
              width: 100%;
              padding: 12px 20px;
              border-bottom: 1px solid #e0e0ea;
              display: flex;
              align-items: center;
              justify-content: space-between;
            `}
          >
            <QSTypography
              css={css`
                font-size: 16px;
                font-weight: 700;
                word-wrap: break-word;
                word-break: break-word;
                color: #2d2d2d;
                font-family: Inter;
              `}
            >
              Create New Group
            </QSTypography>
            <IconButton
              sx={{
                marginRight: '-8px',
              }}
              onClick={onClose}
            >
              <Close
                css={css`
                  font-size: 24px;
                  color: #7a7a7a;
                `}
              />
            </IconButton>
          </Box>
        )}
        <Box
          css={css`
            height: 100%;
            width: 100%;
            overflow-y: auto;
          `}
        >
          <Box
            css={css`
              width: 100%;
              display: flex;
              flex-direction: column;
              justify-content: flex-start;
              align-items: flex-start;
              row-gap: 16px;
              padding: 32px 24px 16px 24px;
            `}
          >
            {renderContent()}
          </Box>
        </Box>

        {isGroupsLimitReached ? (
          <Box
            css={css`
              padding: 12px 18px;
              width: 100%;
              display: flex;
              justify-content: flex-end;
              align-items: center;
              column-gap: 16px;
              border-top: 1px solid #e0e0ea;
            `}
          >
            <QSButton
              variant="contained"
              css={css`
                height: 40px;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 0 20px;
                color: #ffffff;
                background-color: rgba(63, 144, 109, 1);
                border: none;
                outline: none;
                box-shadow: none;
                &:hover {
                  background-color: rgb(44, 100, 76);
                  border: none;
                  outline: none;
                  box-shadow: none;
                }
                &.Mui-disabled {
                  background-color: rgba(63, 144, 109, 0.5);
                  color: rgba(255, 255, 255, 0.7);
                }
              `}
              onClick={handleIncreaseLimit}
              disabled={purchaseAddonLoading || waGroupMetadataLoading}
            >
              <QSTypography
                css={css`
                  font-size: 14px;
                  font-weight: 600;
                  word-wrap: break-word;
                  word-break: break-word;
                `}
              >
                {purchaseAddonLoading ? 'Loading...' : 'Increase Limit'}
              </QSTypography>
            </QSButton>
          </Box>
        ) : (
          <Box
            css={css`
              padding: 12px 18px;
              width: 100%;
              display: flex;
              justify-content: flex-end;
              align-items: center;
              column-gap: 16px;
              border-top: 1px solid #e0e0ea;
            `}
          >
            <QSButton
              variant="contained"
              css={css`
                height: 40px;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 0 20px;
                color: #ffffff;
                background-color: rgba(63, 144, 109, 1);
                border: none;
                outline: none;
                box-shadow: none;
                &:hover {
                  background-color: rgb(44, 100, 76);
                  border: none;
                  outline: none;
                  box-shadow: none;
                }
                &.Mui-disabled {
                  background-color: rgba(63, 144, 109, 0.5);
                  color: rgba(255, 255, 255, 0.7);
                }
              `}
              onClick={handleSubmit}
              disabled={isCreateDisabled || isLoading || waGroupMetadataLoading}
            >
              <QSTypography
                css={css`
                  font-size: 14px;
                  font-weight: 600;
                  word-wrap: break-word;
                  word-break: break-word;
                `}
              >
                {isLoading ? 'Creating...' : 'Create Group'}
              </QSTypography>
            </QSButton>
          </Box>
        )}
      </Box>
    </QSModal>
  );
};

export default CreateGroupModal;
